"use client";

import type { OpenTiming } from "@repo/embed";
import { useCallback, useMemo, useRef } from "react";
import { SKELETON_MARK } from "./painted";

/**
 * Open timings (SDK-PRELOAD.md 3.5): how long the shopper waited for a usable chat, measured by
 * the page and handed to the native host after each show (`timing`). The first one of the
 * document also goes to the server, for the Install screen's median.
 */

/** The page's own marks; ms after `navigationStart`, which is epoch ms. Not rounded yet. */
export type PageMarks = Omit<OpenTiming, "open" | "preloaded">;

/** One show of a page that started hidden. */
export interface Show {
	/** Epoch ms when the host learned of the open (`visibility.at`). */
	at: number;
	/** Epoch ms when the usable chat was presented; null when no frame was confirmed. */
	presentedAt: number | null;
}

/**
 * The `timing` message. `show` is null for a page that started on screen (an SDK before 1.1.0):
 * nobody told it when the tap was. Every value is rounded: the clocks are fractional and the
 * schema takes whole milliseconds.
 */
export function openTiming(marks: PageMarks, show: Show | null): OpenTiming {
	const navigationStart = Math.round(marks.navigationStart);
	const interactive = Math.round(marks.interactive);
	return {
		navigationStart,
		...(marks.responseStart === undefined
			? {}
			: { responseStart: Math.round(marks.responseStart) }),
		firstPaint: Math.round(marks.firstPaint),
		bridgeReady: Math.round(marks.bridgeReady),
		interactive,
		...(show && show.presentedAt !== null
			? { open: Math.max(0, Math.round(show.presentedAt - show.at)) }
			: {}),
		// Epochs, not arrival order: the host holds `visibility` until `ready`, so the
		// message always arrives after interactive.
		preloaded: show !== null && navigationStart + interactive <= show.at,
	};
}

/** The marks, read when the chat became usable (`interactive`: `performance.now()` then). */
export function readMarks(interactive: number): PageMarks {
	const navigation = performance.getEntriesByType("navigation")[0] as
		| PerformanceNavigationTiming
		| undefined;
	const skeleton = performance.getEntriesByName(SKELETON_MARK)[0];
	return {
		navigationStart: performance.timeOrigin,
		...(navigation && navigation.responseStart > 0
			? { responseStart: navigation.responseStart }
			: {}),
		firstPaint: skeleton?.startTime ?? interactive,
		bridgeReady: performance.now(),
		interactive,
	};
}

/** How long a usable chat may wait for its two frames. */
const FRAME_WAIT_MS = 1000;

/**
 * Epoch ms once two animation frames ran: the chat was presented. Null when they have not come
 * in time: a timer must never report a fast open while nothing is on screen, which is exactly
 * how a suspended web view fails.
 */
export function presented(limit = FRAME_WAIT_MS): Promise<number | null> {
	return new Promise((resolve) => {
		const timer = setTimeout(() => resolve(null), limit);
		requestAnimationFrame(() =>
			requestAnimationFrame(() => {
				clearTimeout(timer);
				resolve(Date.now());
			}),
		);
	});
}

export function useOpenTiming({
	native,
	startedHidden,
	post,
	token,
	request,
}: {
	/** A native SDK's web view holds the page: nothing is measured anywhere else. */
	native: boolean;
	startedHidden: boolean;
	/** The bridge's `post`: hands one timing to the host. */
	post: (type: "timing", data: OpenTiming) => unknown;
	/** The shopper session's token, once there is one: the timing is written on that session. */
	token: () => string | null;
	request: (path: string, init?: RequestInit) => Promise<Response>;
}) {
	const marks = useRef<PageMarks | null>(null);
	/** Shows that came before the chat was usable. */
	const early = useRef<number[]>([]);
	/** The document's first timing: the one the server keeps. */
	const first = useRef<OpenTiming | null>(null);
	const reported = useRef(false);

	/** Once per document, as soon as there is a session to write it on. */
	const report = useCallback(() => {
		if (reported.current || !first.current || !token()) {
			return;
		}
		reported.current = true;
		void request("/session/timing", {
			method: "POST",
			headers: { "content-type": "application/json" },
			body: JSON.stringify(first.current),
			// The shopper may close the chat right after opening it.
			keepalive: true,
		}).catch(() => {});
	}, [token, request]);

	const emit = useCallback(
		(timing: OpenTiming) => {
			post("timing", timing);
			first.current ??= timing;
			report();
		},
		[post, report],
	);

	/** The host showed the chat (`visibility` with `at`): one timing per show. */
	const shown = useCallback(
		async (at: number) => {
			const page = marks.current;
			if (!native) {
				return;
			}
			if (!page) {
				early.current.push(at);
				return;
			}
			emit(openTiming(page, { at, presentedAt: await presented() }));
		},
		[native, emit],
	);

	/** The chat became usable: called once, right after `ready` was posted. */
	const interactive = useCallback(
		(at: number) => {
			if (!native || marks.current) {
				return;
			}
			marks.current = readMarks(at);
			if (!startedHidden) {
				emit(openTiming(marks.current, null));
				return;
			}
			for (const show of early.current.splice(0)) {
				void shown(show);
			}
		},
		[native, startedHidden, emit, shown],
	);

	return useMemo(
		() => ({ interactive, shown, report }),
		[interactive, shown, report],
	);
}
